import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { DateInput, fmtDate } from "@/components/DateInput";
import { useI18n } from "@/context/I18nContext";
import { fl } from "@/lib/finI18n";
import { dcoa } from "@/lib/masterI18n";
import { LedgerInput } from "@/components/LedgerInput";
import { OpeningImport } from "@/components/OpeningImport";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Search, X, Download } from "lucide-react";
import { exportRows } from "@/components/TableTools";

const BS = ["asset", "liability", "equity"];
const num = (v) => Number(v || 0);

function FxCells({ c, r, i, cur, fx, onSet, onDel, lang }) {
  const idr = num(r.fx) * (fx?.rate || 0);
  return (
    <>
      <TableCell>
        <div className="flex items-center gap-1"><span className="w-9 shrink-0 text-[11px] font-semibold text-muted-foreground">{cur}</span>
          <Input type="number" className="num h-8 text-right" value={r.fx ?? ""} onChange={(e) => onSet({ fx: e.target.value })} data-testid={`opening-sub-fx-${c.code}-${i}`} /></div>
      </TableCell>
      <TableCell className="flex items-center gap-1">
        <div className="flex-1 text-right leading-tight" data-testid={`opening-sub-fx-idr-${c.code}-${i}`}>
          {fx ? <><div className="num text-xs">Rp {idr.toLocaleString("id-ID", { maximumFractionDigits: 2 })}</div>
            <div className="text-[10px] text-muted-foreground">@ {Number(fx.rate).toLocaleString("id-ID", { maximumFractionDigits: 4 })} · KMK {fx.kmk_no}</div></>
            : <span className="text-[11px] text-rose-600">{fl("kmkMissing", lang)}</span>}
        </div>
        <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={onDel} data-testid={`opening-sub-del-${c.code}-${i}`}><X className="h-4 w-4" /></Button>
      </TableCell>
    </>
  );
}

function SubRow({ clientId, c, r, i, onSet, onDel, cur, fx }) {
  const { lang } = useI18n();
  return (
    <TableRow className="bg-secondary/20" data-testid={`opening-sub-${c.code}-${i}`}>
      <TableCell />
      <TableCell className="pl-8">
        <LedgerInput clientId={clientId} value={r.ledger} testid={`opening-sub-ledger-${c.code}-${i}`} placeholder={fl("ledger", lang)}
          onChange={(v) => onSet({ ledger: v })} onPick={(l) => onSet({ ledger: l.name })} />
      </TableCell>
      {cur ? <FxCells c={c} r={r} i={i} cur={cur} fx={fx} onSet={onSet} onDel={onDel} lang={lang} /> : <>
      <TableCell><Input type="number" className="num h-8 text-right" value={r.debit || ""} onChange={(e) => onSet({ debit: e.target.value })} data-testid={`opening-sub-debit-${c.code}-${i}`} /></TableCell>
      <TableCell className="flex items-center gap-1">
        <Input type="number" className="num h-8 text-right" value={r.credit || ""} onChange={(e) => onSet({ credit: e.target.value })} data-testid={`opening-sub-credit-${c.code}-${i}`} />
        <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={onDel} data-testid={`opening-sub-del-${c.code}-${i}`}><X className="h-4 w-4" /></Button>
      </TableCell></>}
    </TableRow>
  );
}

export function OpeningBalanceTab({ clientId }) {
  const { lang } = useI18n();
  const [year, setYear] = useState(new Date().getFullYear());
  const [coa, setCoa] = useState([]);
  const [vals, setVals] = useState({});
  const [subs, setSubs] = useState({});
  const [q, setQ] = useState("");
  const [start, setStart] = useState(`${new Date().getFullYear()}-01-01`);
  const [fxb, setFxb] = useState({});
  const [fxr, setFxr] = useState({});
  const load = async () => {
    if (!clientId) return;
    const [c, ob, bk] = await Promise.all([api.get(`/coa?client_id=${clientId}`), api.get(`/opening-balances?client_id=${clientId}&year=${year}`), api.get(`/banks?client_id=${clientId}`)]);
    setCoa(c.data);
    setFxb(Object.fromEntries(bk.data.filter((b) => b.code && (b.currency || "IDR") !== "IDR").map((b) => [b.code.toLowerCase(), b.currency])));
    const m = {}; const s = {};
    ob.data.forEach((o) => {
      if (o.ledger) (s[o.coa_id] = s[o.coa_id] || []).push({ ledger: o.ledger, debit: o.debit, credit: o.credit, fx: o.amount_original ?? "" });
      else m[o.coa_id] = { debit: o.debit, credit: o.credit };
    });
    setVals(m); setSubs(s);
    setStart(ob.data[0]?.start_date || `${year}-01-01`);
  };
  useEffect(() => { load(); }, [clientId, year]);
  const setV = (id, k, v) => setVals((s) => ({ ...s, [id]: { ...(s[id] || { debit: 0, credit: 0 }), [k]: v } }));
  const setSub = (id, i, patch) => setSubs((s) => ({ ...s, [id]: s[id].map((r, idx) => (idx === i ? { ...r, ...patch } : r)) }));
  const addSub = (id) => setSubs((s) => ({ ...s, [id]: [...(s[id] || []), { ledger: "", debit: "", credit: "" }] }));
  const delSub = (id, i) => setSubs((s) => ({ ...s, [id]: s[id].filter((_, idx) => idx !== i) }));
  const save = async () => {
    const items = coa.flatMap((c) => [{ coa_id: c.id, debit: num(vals[c.id]?.debit), credit: num(vals[c.id]?.credit) },
      ...(subs[c.id] || []).filter((r) => r.ledger.trim()).map((r) => (fxOf(r)
        ? { coa_id: c.id, ledger: r.ledger.trim(), amount_original: num(r.fx), ...sideOf(r) }
        : { coa_id: c.id, ledger: r.ledger.trim(), debit: num(r.debit), credit: num(r.credit) }))]);
    try { await api.post("/opening-balances", { client_id: clientId, year, items, start_date: start }); toast.success(fl("obSaved", lang)); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const fxOf = (r) => fxb[(r.ledger || "").trim().toLowerCase()] || null;
  const sideOf = (r) => {
    const cur = fxOf(r);
    if (!cur) return { debit: num(r.debit), credit: num(r.credit) };
    const v = Math.round(num(r.fx) * (fxr[cur]?.rate || 0) * 100) / 100;
    return { debit: Math.max(v, 0), credit: Math.max(-v, 0) };
  };
  const sum = (k) => coa.reduce((s, c) => s + num(vals[c.id]?.[k]) + (subs[c.id] || []).reduce((a, r) => a + sideOf(r)[k], 0), 0);
  const td = sum("debit"); const tc = sum("credit");
  const years = Array.from({ length: 11 }, (_, i) => new Date().getFullYear() - i);
  const asOf = start > `${year}-01-01` ? new Date(new Date(`${start}T00:00:00Z`).getTime() - 864e5).toISOString().slice(0, 10) : `${year}-01-01`;
  useEffect(() => {
    const curs = [...new Set(Object.values(fxb))];
    if (!curs.length) { setFxr({}); return; }
    Promise.all(curs.map((cur) => api.get(`/kurs/kmk/rate?currency=${cur}&date=${asOf}`).then((r) => [cur, r.data]).catch(() => [cur, null])))
      .then((p) => setFxr(Object.fromEntries(p)));
  }, [fxb, asOf]);
  const loc = lang === "en" ? "en-US" : lang === "zh" ? "zh-CN" : "id-ID";
  const ql = q.trim().toLowerCase();
  const shown = coa.filter((c) => !ql || `${c.code} ${c.name} ${c.name_en || ""} ${c.name_zh || ""} ${(subs[c.id] || []).map((r) => r.ledger).join(" ")}`.toLowerCase().includes(ql));
  return (
    <Card className="p-4" data-testid="opening-balance-tab">
      <div className="flex items-start justify-between mb-3 flex-wrap gap-3">
        <div className="space-y-1">
          <h2 className="font-head font-semibold">{fl("obTitle", lang)}</h2>
          <p className="text-[11px] text-muted-foreground max-w-2xl" data-testid="opening-hint">{fl("obHint", lang)}</p>
          <p className="text-[11px] text-muted-foreground max-w-2xl" data-testid="opening-ledger-hint">{fl("obLedgerHint", lang)}</p>
          <p className="text-[11px] text-primary max-w-2xl" data-testid="opening-asof">{fl("obAsOf", lang).replace("{d}", fmtDate(asOf)).replace("{s}", fmtDate(start))}</p>
          {Object.keys(fxb).length > 0 && <p className="text-[11px] text-amber-700 max-w-2xl" data-testid="opening-fx-hint">{fl("obFxHint", lang).replace("{d}", fmtDate(asOf))} ({Object.keys(fxb).map((k) => k.toUpperCase()).join(", ")})</p>}
          <p className={`text-xs ${Math.round(td - tc) === 0 ? "text-emerald-600" : "text-rose-600"}`} data-testid="opening-balance-status">
            {fl("debit", lang)} {td.toLocaleString(loc)} · {fl("credit", lang)} {tc.toLocaleString(loc)} — {fl(Math.round(td - tc) === 0 ? "obBalanced" : "obUnbalanced", lang)}
          </p>
        </div>
        <div className="flex gap-2 items-end flex-wrap">
          <div className="relative"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input placeholder={fl("tSearch", lang)} value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 h-9 w-44" data-testid="opening-search" /></div>
          <div className="space-y-1"><label className="text-xs text-muted-foreground">{fl("lkYear", lang)}</label>
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="w-28" data-testid="opening-year"><SelectValue /></SelectTrigger>
              <SelectContent>{years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="space-y-1"><label className="text-xs text-muted-foreground">{fl("obStart", lang)}</label>
            <DateInput className="w-40" min={`${year}-01-01`} max={`${year}-12-31`} value={start}
              onChange={(e) => setStart(e.target.value || `${year}-01-01`)} data-testid="opening-start-date" /></div>
          <OpeningImport clientId={clientId} year={year} start={start} onDone={load} />
          <Button variant="outline" data-testid="opening-export" onClick={() => {
            const out = shown.flatMap((c) => [
              { code: c.code, name: c.name, ledger: "", debit: num(vals[c.id]?.debit), credit: num(vals[c.id]?.credit) },
              ...(subs[c.id] || []).filter((r) => r.ledger.trim()).map((r) => ({ code: c.code, name: c.name, ledger: r.ledger, ...sideOf(r) }))]).filter((x) => x.debit || x.credit);
            if (!out.length) { toast.error(lang === "en" ? "No opening balances yet" : lang === "zh" ? "暂无期初余额" : "Belum ada saldo awal"); return; }
            exportRows(out, [["Kode Akun", "code"], ["Nama Akun", "name"], ["Buku Besar", "ledger"], ["Debit", "debit"], ["Kredit", "credit"]], `saldo_awal_${year}`);
          }}>
            <Download className="h-4 w-4 mr-1" />Ekspor</Button>
          <Button onClick={save} data-testid="opening-save">{fl("obSave", lang)}</Button>
        </div>
      </div>
      <div className="overflow-auto rounded-md border border-border">
        <Table>
          <TableHeader><TableRow><TableHead className="w-20">{fl("code", lang)}</TableHead><TableHead>{fl("account", lang)}</TableHead><TableHead className="w-40 text-right">{fl("debit", lang)}</TableHead><TableHead className="w-48 text-right">{fl("credit", lang)}</TableHead></TableRow></TableHeader>
          <TableBody>
            {shown.map((c) => [
              <TableRow key={c.id}>
                <TableCell className="num text-xs">{c.code}</TableCell>
                <TableCell className="text-sm">
                  <span className="flex items-center justify-between gap-2">{dcoa(c.code, c.name, lang, c)}
                    {BS.includes(c.type) && <Button variant="ghost" size="sm" className="h-6 px-2 text-[11px]" onClick={() => addSub(c.id)} data-testid={`opening-add-ledger-${c.code}`}>{fl("obAddLedger", lang)}</Button>}</span>
                </TableCell>
                <TableCell><Input type="number" className="num h-8 text-right" value={vals[c.id]?.debit || ""} onChange={(e) => setV(c.id, "debit", e.target.value)} data-testid={`opening-debit-${c.code}`} /></TableCell>
                <TableCell><Input type="number" className="num h-8 text-right" value={vals[c.id]?.credit || ""} onChange={(e) => setV(c.id, "credit", e.target.value)} data-testid={`opening-credit-${c.code}`} /></TableCell>
              </TableRow>,
              ...(subs[c.id] || []).map((r, i) => <SubRow key={`${c.id}-${i}`} clientId={clientId} c={c} r={r} i={i} cur={fxOf(r)} fx={fxr[fxOf(r)]} onSet={(p) => setSub(c.id, i, p)} onDel={() => delSub(c.id, i)} />),
            ])}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}
